import DateTimeDisplay from '@/components/DateTime';
import { currencyFormatter, kwhPriceFormatter } from '@/helpers/currencyFormatter';
import { percentageFormatter } from '@/helpers/percentageFormatter';
import { Badge, Box } from '@palmetto/palmetto-components';
import { Link } from 'react-router-dom';

import { EquipmentType, EquipmentTypeLabels, PriceSheetStatus, ProgramType } from 'types';

export const mappingsColumnConfig = {
  baseStart: [
    {
      heading: 'Status',
      dataKey: 'status',
      width: 125,
      render: (_cell: any, row: any) => (
        <Badge
          variant={
            row.status === PriceSheetStatus.active
              ? 'success'
              : row.status === PriceSheetStatus.archived
                ? 'warning'
                : 'danger'
          }
          message={row.status}
        />
      ),
    },
    { heading: 'org', dataKey: 'organizationName' },
    { heading: 'state', dataKey: 'state' },
  ],
  baseEnd: [
    {
      heading: 'Created',
      dataKey: 'createdDate',
      width: 150,
      render: (_cell: any, row: any) => <DateTimeDisplay value={row?.createdDate} dateFormat={'DD'} excludeTime />,
    },
    {
      heading: 'Archived',
      dataKey: 'archivedDate',
      width: 150,
      render: (_cell: any, row: any) => <DateTimeDisplay value={row?.archivedDate} dateFormat={'DD'} excludeTime />,
    },
  ],
  [ProgramType.solar]: [
    {
      heading: 'utility',
      dataKey: 'utility',
      render: (cell: any) => <Box>{cell?.name}</Box>,
    },
    {
      heading: 'Price Sheet',
      dataKey: 'priceSheetName',
      render: (cell: any, row: any) => (
        <Link
          to={`/admin/pricing/${row.programType}/price-sheet/${row.priceSheetId}`}
          style={{ textDecoration: 'underline' }}
        >
          {cell}
        </Link>
      ),
    },
    {
      heading: 'esc min',
      dataKey: 'escalationRateMin',
      headerClassName: 'wrap',
      render: (cell: any) => {
        return <div>{percentageFormatter.format(cell)}</div>;
      },
    },
    {
      heading: 'esc max',
      dataKey: 'escalationRateMax',
      headerClassName: 'wrap',
      render: (cell: any) => {
        return <div>{percentageFormatter.format(cell)}</div>;
      },
    },
    {
      heading: 'pv-only min',
      dataKey: 'pvOnlyMin',
      headerClassName: 'wrap',
      render: (cell: any) => {
        return <div>{kwhPriceFormatter.format(cell)}</div>;
      },
    },
    {
      heading: 'pv-only max',
      dataKey: 'pvOnlyCeiling',
      headerClassName: 'wrap',
      render: (cell: any) => {
        return <div>{kwhPriceFormatter.format(cell)}</div>;
      },
    },
    {
      heading: 'pv-only zero escalator max',
      dataKey: 'zeroEscalatorCeiling',
      headerClassName: 'wrap',
      render: (cell: any) => {
        return cell && <div>{kwhPriceFormatter.format(cell)}</div>;
      },
    },
    {
      heading: 'absolute max',
      dataKey: 'absoluteCeiling',
      headerClassName: 'wrap',
      render: (cell: any) => {
        return <div>{kwhPriceFormatter.format(cell)}</div>;
      },
    },
    {
      heading: 'construction adders',
      dataKey: 'constructionAdders',
      headerClassName: 'wrap',
      render: (cell: any) => {
        return (
          <Box childGap="xs">
            {cell?.map((adder: any) => (
              <Box key={adder.name}>{adder.name}</Box>
            ))}
          </Box>
        );
      },
    },
    {
      heading: 'modifiers',
      render: (_cell: any, row: any) => (
        <Box childGap="xs">
          {row.holdback && <Box>{row.holdback.name}</Box>}
          {row.energyCommunity && <Box>{row.energyCommunity.name}</Box>}
          {row.ppwModifier && <Box>{row.ppwModifier.name}</Box>}
          {row.domesticContentModifier && <Box>{row.domesticContentModifier.name}</Box>}
        </Box>
      ),
    },
  ],
  [ProgramType.newHomes]: [
    {
      heading: 'utility',
      dataKey: 'utility',
      render: (cell: any) => <Box>{cell?.name}</Box>,
    },
    {
      heading: 'Price Sheet',
      dataKey: 'priceSheetName',
      render: (cell: any, row: any) => (
        <Link
          to={`/admin/pricing/${row.programType}/price-sheet/${row.priceSheetId}`}
          style={{ textDecoration: 'underline' }}
        >
          {cell}
        </Link>
      ),
    },
    {
      heading: 'esc min',
      dataKey: 'escalationRateMin',
      headerClassName: 'wrap',
      render: (cell: any) => {
        return <div>{percentageFormatter.format(cell)}</div>;
      },
    },
    {
      heading: 'esc max',
      dataKey: 'escalationRateMax',
      headerClassName: 'wrap',
      render: (cell: any) => {
        return <div>{percentageFormatter.format(cell)}</div>;
      },
    },
    {
      heading: 'pv-only min',
      dataKey: 'pvOnlyMin',
      headerClassName: 'wrap',
      render: (cell: any) => {
        return <div>{kwhPriceFormatter.format(cell)}</div>;
      },
    },
    {
      heading: 'pv-only max',
      dataKey: 'pvOnlyCeiling',
      headerClassName: 'wrap',
      render: (cell: any) => {
        return <div>{kwhPriceFormatter.format(cell)}</div>;
      },
    },
    {
      heading: 'pv-only zero escalator max',
      dataKey: 'zeroEscalatorCeiling',
      headerClassName: 'wrap',
      render: (cell: any) => {
        return cell && <div>{kwhPriceFormatter.format(cell)}</div>;
      },
    },
    {
      heading: 'absolute max',
      dataKey: 'absoluteCeiling',
      headerClassName: 'wrap',
      render: (cell: any) => {
        return <div>{kwhPriceFormatter.format(cell)}</div>;
      },
    },
    {
      heading: 'construction adders',
      dataKey: 'constructionAdders',
      headerClassName: 'wrap',
      render: (cell: any) => {
        return (
          <Box childGap="xs">
            {cell?.map((adder: any) => (
              <Box key={adder.name}>{adder.name}</Box>
            ))}
          </Box>
        );
      },
    },
    {
      heading: 'modifiers',
      render: (_cell: any, row: any) => (
        <Box childGap="xs">
          {row.holdback && <Box>{row.holdback.name}</Box>}
          {row.energyCommunity && <Box>{row.energyCommunity.name}</Box>}
          {row.ppwModifier && <Box>{row.ppwModifier.name}</Box>}
          {row.domesticContentModifier && <Box>{row.domesticContentModifier.name}</Box>}
        </Box>
      ),
    },
  ],
  [ProgramType.hvac]: [
    {
      heading: 'Price Sheet',
      dataKey: 'priceSheetName',
      render: (cell: any, row: any) => (
        <Link
          to={`/admin/pricing/${row.programType}/price-sheet/${row.priceSheetId}`}
          style={{ textDecoration: 'underline' }}
        >
          {cell}
        </Link>
      ),
    },
    {
      heading: 'esc min',
      dataKey: 'escalationRateMin',
      headerClassName: 'wrap',
      render: (cell: any) => {
        return <div>{percentageFormatter.format(cell)}</div>;
      },
    },
    {
      heading: 'esc max',
      dataKey: 'escalationRateMax',
      headerClassName: 'wrap',
      render: (cell: any) => {
        return <div>{percentageFormatter.format(cell)}</div>;
      },
    },
    {
      heading: 'min system cost',
      dataKey: 'minSystemCost',
      headerClassName: 'wrap',
    },
    {
      heading: 'max system cost',
      dataKey: 'maxSystemCost',
      headerClassName: 'wrap',
    },
    { heading: 'Min Term', dataKey: 'minTerm', headerClassName: 'wrap' },
    { heading: 'Max Term', dataKey: 'maxTerm', headerClassName: 'wrap' },
    {
      heading: 'Warranty Cost Included',
      dataKey: 'isWarrantyCostIncluded',
      headerClassName: 'wrap',
      render: (cell: any) => <div>{cell ? 'Yes' : 'No'}</div>,
    },
    {
      heading: 'Estimated Warranty Cost',
      dataKey: 'estimatedWarrantyCost',
      headerClassName: 'wrap',
      render: (cell: any) => currencyFormatter.format(cell || 0),
    },
  ],
  [ProgramType.waterHeater]: [
    {
      heading: 'Price Sheet',
      dataKey: 'priceSheetName',
      render: (cell: any, row: any) => (
        <Link
          to={`/admin/pricing/${row.programType}/price-sheet/${row.priceSheetId}`}
          style={{ textDecoration: 'underline' }}
        >
          {cell}
        </Link>
      ),
    },
    {
      heading: 'esc min',
      dataKey: 'escalationRateMin',
      headerClassName: 'wrap',
      render: (cell: any) => {
        return <div>{percentageFormatter.format(cell)}</div>;
      },
    },
    {
      heading: 'esc max',
      dataKey: 'escalationRateMax',
      headerClassName: 'wrap',
      render: (cell: any) => {
        return <div>{percentageFormatter.format(cell)}</div>;
      },
    },
    {
      heading: 'min system cost',
      dataKey: 'minSystemCost',
      headerClassName: 'wrap',
    },
    {
      heading: 'max system cost',
      dataKey: 'maxSystemCost',
      headerClassName: 'wrap',
    },
    { heading: 'Min Term', dataKey: 'minTerm', headerClassName: 'wrap' },
    { heading: 'Max Term', dataKey: 'maxTerm', headerClassName: 'wrap' },
    {
      heading: 'Warranty Cost Included',
      dataKey: 'isWarrantyCostIncluded',
      headerClassName: 'wrap',
      render: (cell: any) => <div>{cell ? 'Yes' : 'No'}</div>,
    },
    {
      heading: 'Estimated Warranty Cost',
      dataKey: 'estimatedWarrantyCost',
      headerClassName: 'wrap',
      render: (cell: any) => currencyFormatter.format(cell || 0),
    },
  ],
  [ProgramType.generator]: [
    {
      heading: 'Price Sheet',
      dataKey: 'priceSheetName',
      render: (cell: any, row: any) => (
        <Link
          to={`/admin/pricing/${row.programType}/price-sheet/${row.priceSheetId}`}
          style={{ textDecoration: 'underline' }}
        >
          {cell}
        </Link>
      ),
    },
    {
      heading: 'esc min',
      dataKey: 'escalationRateMin',
      headerClassName: 'wrap',
      render: (cell: any) => {
        return <div>{percentageFormatter.format(cell)}</div>;
      },
    },
    {
      heading: 'esc max',
      dataKey: 'escalationRateMax',
      headerClassName: 'wrap',
      render: (cell: any) => {
        return <div>{percentageFormatter.format(cell)}</div>;
      },
    },
    {
      heading: 'min system cost',
      dataKey: 'minSystemCost',
      headerClassName: 'wrap',
    },
    {
      heading: 'max system cost',
      dataKey: 'maxSystemCost',
      headerClassName: 'wrap',
    },
    { heading: 'Min Term', dataKey: 'minTerm', headerClassName: 'wrap' },
    { heading: 'Max Term', dataKey: 'maxTerm', headerClassName: 'wrap' },
  ],
  [ProgramType.doePr]: [
    {
      heading: 'utility',
      dataKey: 'utility',
      render: (cell: any) => <Box>{cell?.name}</Box>,
    },
    {
      heading: 'Price Sheet',
      dataKey: 'priceSheetName',
      render: (cell: any, row: any) => (
        <Link
          to={`/admin/pricing/${row.programType}/price-sheet/${row.priceSheetId}`}
          style={{ textDecoration: 'underline' }}
        >
          {cell}
        </Link>
      ),
    },
    {
      heading: 'esc min',
      dataKey: 'escalationRateMin',
      headerClassName: 'wrap',
      render: (cell: any) => {
        return <div>{percentageFormatter.format(cell)}</div>;
      },
    },
    {
      heading: 'esc max',
      dataKey: 'escalationRateMax',
      headerClassName: 'wrap',
      render: (cell: any) => {
        return <div>{percentageFormatter.format(cell)}</div>;
      },
    },
    {
      heading: 'system size min',
      dataKey: 'minSystemSize',
      headerClassName: 'wrap',
    },
    {
      heading: 'system size max',
      dataKey: 'maxSystemSize',
      headerClassName: 'wrap',
    },
    {
      heading: 'battery capacity min',
      dataKey: 'minBatteryCapacity',
      headerClassName: 'wrap',
    },
    {
      heading: 'battery capacity max',
      dataKey: 'maxBatteryCapacity',
      headerClassName: 'wrap',
    },
    {
      heading: 'construction adders',
      dataKey: 'constructionAdders',
      headerClassName: 'wrap',
      render: (cell: any) => {
        return (
          <Box childGap="xs">
            {cell?.map((adder: any) => (
              <Box key={adder.name}>{adder.name}</Box>
            ))}
          </Box>
        );
      },
    },
  ],
  [ProgramType.energyBackup]: [
    {
      heading: 'utility',
      dataKey: 'utility',
      render: (cell: any) => <Box>{cell?.name}</Box>,
    },
    {
      heading: 'Equipment Types',
      dataKey: 'equipmentTypes',
      render: (cell: EquipmentType[]) => (
        <Box childGap="xs">
          {cell?.map((type: EquipmentType) => (
            <Box key={type}>{EquipmentTypeLabels[type]}</Box>
          ))}
        </Box>
      ),
    },
    {
      heading: 'Price Sheet',
      dataKey: 'priceSheetName',
      render: (cell: any, row: any) => (
        <Link
          to={`/admin/pricing/${row.programType}/price-sheet/${row.priceSheetId}`}
          style={{ textDecoration: 'underline' }}
        >
          {cell}
        </Link>
      ),
    },
    {
      heading: 'esc min',
      dataKey: 'escalationRateMin',
      headerClassName: 'wrap',
      render: (cell: any) => {
        return <div>{percentageFormatter.format(cell)}</div>;
      },
    },
    {
      heading: 'esc max',
      dataKey: 'escalationRateMax',
      headerClassName: 'wrap',
      render: (cell: any) => {
        return <div>{percentageFormatter.format(cell)}</div>;
      },
    },
    {
      heading: 'min system cost',
      dataKey: 'minSystemCost',
      headerClassName: 'wrap',
    },
    {
      heading: 'max system cost',
      dataKey: 'maxSystemCost',
      headerClassName: 'wrap',
    },
    { heading: 'Min Term', dataKey: 'minTerm', headerClassName: 'wrap' },
    { heading: 'Max Term', dataKey: 'maxTerm', headerClassName: 'wrap' },
    {
      heading: 'construction adders',
      dataKey: 'constructionAdders',
      headerClassName: 'wrap',
      render: (cell: any) => {
        return (
          <Box childGap="xs">
            {cell?.map((adder: any) => (
              <Box key={adder.name}>{adder.name}</Box>
            ))}
          </Box>
        );
      },
    },
  ],
  [ProgramType.geothermal]: [
    {
      heading: 'Price Sheet',
      dataKey: 'priceSheetName',
      render: (cell: any, row: any) => (
        <Link
          to={`/admin/pricing/${row.programType}/price-sheet/${row.priceSheetId}`}
          style={{ textDecoration: 'underline' }}
        >
          {cell}
        </Link>
      ),
    },
    {
      heading: 'esc min',
      dataKey: 'escalationRateMin',
      headerClassName: 'wrap',
      render: (cell: any) => {
        return <div>{percentageFormatter.format(cell)}</div>;
      },
    },
    {
      heading: 'esc max',
      dataKey: 'escalationRateMax',
      headerClassName: 'wrap',
      render: (cell: any) => {
        return <div>{percentageFormatter.format(cell)}</div>;
      },
    },
    {
      heading: 'min system cost',
      dataKey: 'minSystemCost',
      headerClassName: 'wrap',
    },
    {
      heading: 'max system cost',
      dataKey: 'maxSystemCost',
      headerClassName: 'wrap',
    },
    { heading: 'Min Term', dataKey: 'minTerm', headerClassName: 'wrap' },
    { heading: 'Max Term', dataKey: 'maxTerm', headerClassName: 'wrap' },
  ],
  [ProgramType.newCommunity]: [],
  [ProgramType.consumerSolar]: [
    {
      heading: 'utility',
      dataKey: 'utility',
      render: (cell: any) => <Box>{cell?.name}</Box>,
    },
    {
      heading: 'Price Sheet',
      dataKey: 'priceSheetName',
      render: (cell: any, row: any) => (
        <Link
          to={`/admin/pricing/${row.programType}/price-sheet/${row.priceSheetId}`}
          style={{ textDecoration: 'underline' }}
        >
          {cell}
        </Link>
      ),
    },
    {
      heading: 'esc min',
      dataKey: 'escalationRateMin',
      headerClassName: 'wrap',
      render: (cell: any) => {
        return <div>{percentageFormatter.format(cell)}</div>;
      },
    },
    {
      heading: 'esc max',
      dataKey: 'escalationRateMax',
      headerClassName: 'wrap',
      render: (cell: any) => {
        return <div>{percentageFormatter.format(cell)}</div>;
      },
    },
    {
      heading: 'system size min',
      dataKey: 'minSystemSize',
      headerClassName: 'wrap',
    },
    {
      heading: 'system size max',
      dataKey: 'maxSystemSize',
      headerClassName: 'wrap',
    },
    {
      heading: 'production factor min',
      dataKey: 'minProductionFactor',
      headerClassName: 'wrap',
    },
    {
      heading: 'production factor max',
      dataKey: 'maxProductionFactor',
      headerClassName: 'wrap',
    },
    {
      heading: 'construction adders',
      dataKey: 'constructionAdders',
      headerClassName: 'wrap',
      render: (cell: any) => {
        return (
          <Box childGap="xs">
            {cell?.map((adder: any) => (
              <Box key={adder.name}>{adder.name}</Box>
            ))}
          </Box>
        );
      },
    },
  ],
};
